import { request, requestDownload, requestText } from './client'
import { env } from '@/lib/env'
import type {
  StaffFeedbackItem,
  StaffFeedbackPage,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
  TriageFeedbackRequest,
  ZoneHistoryPage,
  ZoneMessageIndex,
  AccountBalance,
  BillingSummary,
  OverLimit,
  PoliceRecharge,
  CheckoutSession,
  SubscribeResult,
  BillingConfig,
  Invoice,
  InvoicePage,
  InvoiceKind,
  UpcomingInvoice,
  PaymentMethodCard,
  BillingDetails,
  BillingAddress,
  TaxId,
  PlanChangePreview,
  PlanChangeResult,
  SubscriptionState,
  BalanceTransactionRow,
  Refundable,
  RefundRequest,
  ConversationIndex,
  SearchResults,
  DeleteRequest,
  DeletionRequested,
  AccountAddon,
  AccountResponse,
  AccountSummary,
  AccountUser,
  SecretEvent,
  DownloadEventDto,
  SecureFileDto,
  UploadsPage,
  ChannelMember,
  ChannelSummary,
  ConnectionSummary,
  CreateChannelRequest,
  CreateZoneRequest,
  InviteUserRequest,
  JoinZoneRequest,
  MeResponse,
  PauseConnectionRequest,
  Plan,
  PoliceSettings,
  PoliceModelOption,
  QueuedMessage,
  RegisterRequest,
  ReissueResponse,
  ReleaseResponse,
  RotateTokenResponse,
  SkillVendor,
  SendMessageRequest,
  SendMessageResponse,
  ToolingInfo,
  UnpauseConnectionRequest,
  UnpauseResponse,
  UpdatePoliceRequest,
  UpdateUserRequest,
  UpdateZoneRequest,
  VerifyRequest,
  VerifyResponse,
  InvitationPreview,
  ZoneMemberSummary,
  ZoneStateFilter,
  ZoneSummary,
} from './types'

/**
 * One function per REST endpoint in ARCHITECTURE.md §18. Nothing outside this
 * module builds an API URL by hand.
 *
 * `{zoneId}` is always the zone **UUID**. The `public_id` belongs to the MCP
 * URL and appears nowhere in `/api`.
 */

/** Zone ids and user ids arrive from the API but are still escaped on the way out. */
function seg(value: string): string {
  return encodeURIComponent(value)
}

function withSignal(signal: AbortSignal | undefined) {
  return signal === undefined ? {} : { signal }
}

/* --- public (permitAll) --------------------------------------------------- */

/** `POST /api/auth/register` -> 202 Accepted, verification mail sent. */
export async function register(body: RegisterRequest): Promise<void> {
  await request<void>('/auth/register', { method: 'POST', body, authenticated: false })
}

/** `POST /api/auth/verify` -> creates the user, account and its `main` zone. */
export function verify(body: VerifyRequest): Promise<VerifyResponse> {
  return request<VerifyResponse>('/auth/verify', {
    method: 'POST',
    body,
    authenticated: false,
  })
}

/**
 * `POST /api/auth/invitation` — the invited name, account and role behind a link, without
 * redeeming it (bug-221). A self-registration link answers `{ invited: false }`.
 */
export function invitationPreview(token: string): Promise<InvitationPreview> {
  return request<InvitationPreview>('/auth/invitation', {
    method: 'POST',
    body: { token },
    authenticated: false,
  })
}

/* --- identity ------------------------------------------------------------- */

/** `GET /api/me` */
export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>('/me', withSignal(signal))
}

/** `GET /api/account` — account, plan, effective limits and state. */
export function getAccount(signal?: AbortSignal): Promise<AccountResponse> {
  return request<AccountResponse>('/account', withSignal(signal))
}

/** `GET /api/plans` — the catalogue, for display. */
export function listPlans(signal?: AbortSignal): Promise<Plan[]> {
  return request<Plan[]>('/plans', withSignal(signal))
}

/** `POST /api/me/token/rotate` — returns the new raw token exactly once. */
export function rotateToken(): Promise<RotateTokenResponse> {
  return request<RotateTokenResponse>('/me/token/rotate', { method: 'POST' })
}

/* --- zones ---------------------------------------------------------------- */

/** `GET /api/zones?state=active|all|archived` */
export function listZones(
  state: ZoneStateFilter = 'active',
  signal?: AbortSignal,
): Promise<ZoneSummary[]> {
  return request<ZoneSummary[]>(`/zones?state=${state}`, withSignal(signal))
}

/** `GET /api/zones/{zoneId}` — 404 when the caller cannot reach it. */
export function getZone(zoneId: string, signal?: AbortSignal): Promise<ZoneSummary> {
  return request<ZoneSummary>(`/zones/${seg(zoneId)}`, withSignal(signal))
}

/** `POST /api/zones` (ADMIN) -> 201 */
export function createZone(body: CreateZoneRequest): Promise<ZoneSummary> {
  return request<ZoneSummary>('/zones', { method: 'POST', body })
}

/** `PATCH /api/zones/{zoneId}` (ADMIN) */
export function updateZone(zoneId: string, body: UpdateZoneRequest): Promise<ZoneSummary> {
  return request<ZoneSummary>(`/zones/${seg(zoneId)}`, { method: 'PATCH', body })
}

/** `POST /api/zones/{zoneId}/deactivate` (ADMIN) — disconnects everything in it. */
export async function deactivateZone(zoneId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/deactivate`, { method: 'POST' })
}

/** `POST /api/zones/{zoneId}/activate` (ADMIN) — refused if ARCHIVED. */
export async function activateZone(zoneId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/activate`, { method: 'POST' })
}

/** `POST /api/zones/{zoneId}/archive` (ADMIN) — terminal, no way back. */
export async function archiveZone(zoneId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/archive`, { method: 'POST' })
}

/* --- zone membership ------------------------------------------------------ */

/** `GET /api/zones/{zoneId}/members` (ADMIN) */
export function listZoneMembers(
  zoneId: string,
  signal?: AbortSignal,
): Promise<ZoneMemberSummary[]> {
  return request<ZoneMemberSummary[]>(`/zones/${seg(zoneId)}/members`, withSignal(signal))
}

/** `PUT /api/zones/{zoneId}/members/{userId}` (ADMIN) -> 204 */
export async function grantZoneMember(zoneId: string, userId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/members/${seg(userId)}`, { method: 'PUT' })
}

/** `DELETE /api/zones/{zoneId}/members/{userId}` (ADMIN) -> 204 */
export async function revokeZoneMember(zoneId: string, userId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/members/${seg(userId)}`, { method: 'DELETE' })
}

/* --- account users -------------------------------------------------------- */

/** `GET /api/users` (ADMIN) */
export function listUsers(signal?: AbortSignal): Promise<AccountUser[]> {
  return request<AccountUser[]>('/users', withSignal(signal))
}

/** `POST /api/users` (ADMIN) — sends the invitation mail. */
export function inviteUser(body: InviteUserRequest): Promise<AccountUser> {
  return request<AccountUser>('/users', { method: 'POST', body })
}

/** `PATCH /api/users/{userId}` (ADMIN) — ADMIN <-> USER. */
export function updateUser(userId: string, body: UpdateUserRequest): Promise<AccountUser> {
  return request<AccountUser>(`/users/${seg(userId)}`, { method: 'PATCH', body })
}

/** `POST /api/users/{userId}/deactivate` (ADMIN) */
export async function deactivateUser(userId: string): Promise<void> {
  await request<void>(`/users/${seg(userId)}/deactivate`, { method: 'POST' })
}

/** `POST /api/users/{userId}/activate` (ADMIN) */
export async function activateUser(userId: string): Promise<void> {
  await request<void>(`/users/${seg(userId)}/activate`, { method: 'POST' })
}

/* --- connections ---------------------------------------------------------- */

/** `GET /api/zones/{zoneId}/connections` */
export function listConnections(
  zoneId: string,
  signal?: AbortSignal,
): Promise<ConnectionSummary[]> {
  return request<ConnectionSummary[]>(`/zones/${seg(zoneId)}/connections`, withSignal(signal))
}

/**
 * `DELETE /api/zones/{zoneId}/connections/{id}` (ADMIN) — force-disconnect.
 *
 * Since IMPROVEMENTS_1 this only takes the agent offline: it keeps its name, its queue and its
 * channel memberships, and returns as itself with its connection id. Freeing the name is
 * {@link releaseConnection}.
 */
export async function disconnectConnection(zoneId: string, id: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/connections/${seg(id)}`, { method: 'DELETE' })
}

/**
 * `POST /api/zones/{zoneId}/connections/{id}/release` (zone administrator, WI9) -> 200.
 *
 * The destructive one. Frees the name, discards the unread queue, kills the identity's
 * credentials; history is kept as `history_name`. 404 when the id is not a releasable identity of
 * this zone (a human, already released, unknown) — so releasing twice is 404, not 200.
 */
export function releaseConnection(zoneId: string, id: string): Promise<ReleaseResponse> {
  return request<ReleaseResponse>(`/zones/${seg(zoneId)}/connections/${seg(id)}/release`, {
    method: 'POST',
  })
}

/**
 * `POST /api/zones/{zoneId}/connections/{id}/reissue` -> 200, the identity's owner or the zone
 * administrator. Mints a new connection id and returns it once; the old id stops working. 404 when
 * the row is not a live identity of this zone, 403 when the caller neither owns it nor administers.
 */
export function reissueConnection(zoneId: string, id: string): Promise<ReissueResponse> {
  return request<ReissueResponse>(`/zones/${seg(zoneId)}/connections/${seg(id)}/reissue`, {
    method: 'POST',
  })
}

/* --- pausing (§19) -------------------------------------------------------- */

/**
 * `POST /api/zones/{zoneId}/connections/{id}/pause` (zone administrator) -> 200.
 *
 * Not a disconnect. The agent stays CONNECTED and its tools keep answering; what
 * stops is delivery in both directions. It is also told, by a message that
 * bypasses the hold it just created, and the zone administrator is emailed.
 * Idempotent — re-pausing is 200 and re-sends nothing.
 */
export function pauseConnection(
  zoneId: string,
  id: string,
  body: PauseConnectionRequest,
): Promise<ConnectionSummary> {
  return request<ConnectionSummary>(`/zones/${seg(zoneId)}/connections/${seg(id)}/pause`, {
    method: 'POST',
    body,
  })
}

/**
 * `POST /api/zones/{zoneId}/connections/{id}/unpause` (zone administrator) -> 200.
 *
 * Both dispositions are required by the server. Unpausing something that was not
 * paused is a 200 carrying `was_paused: false`.
 */
export function unpauseConnection(
  zoneId: string,
  id: string,
  body: UnpauseConnectionRequest,
): Promise<UnpauseResponse> {
  return request<UnpauseResponse>(`/zones/${seg(zoneId)}/connections/${seg(id)}/unpause`, {
    method: 'POST',
    body,
  })
}

/* --- police (§19) --------------------------------------------------------- */

/**
 * `GET /api/zones/{zoneId}/police` (zone administrator).
 *
 * Never 404: the seat is materialised, disabled, with the system-prefilled
 * prompt on first read. Readable for a DEACTIVATED or ARCHIVED zone too.
 */
export function getPolice(zoneId: string, signal?: AbortSignal): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police`, withSignal(signal))
}

/** `PUT /api/zones/{zoneId}/police` — omitted fields are left alone, never cleared. */
export function updatePolice(zoneId: string, body: UpdatePoliceRequest): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police`, { method: 'PUT', body })
}

/**
 * `DELETE /api/zones/{zoneId}/police/api-key` — the only way to clear the key.
 *
 * Answers 200 with the updated settings rather than 204, because the console's
 * next render needs the cleared prefix.
 */
/** `GET /api/zones/{zoneId}/secrets` — the record that secrets were passed; never a value. */
export function getSecretEvents(zoneId: string, signal?: AbortSignal): Promise<SecretEvent[]> {
  return request<SecretEvent[]>(`/zones/${seg(zoneId)}/secrets`, withSignal(signal))
}

/** `PUT /api/account/secret-lifetime` — ADMIN only; bounds enforced server-side. */
export function setSecretLifetime(lifetimeSeconds: number): Promise<AccountSummary> {
  return request<AccountSummary>('/account/secret-lifetime', {
    method: 'PUT',
    body: { lifetime_seconds: lifetimeSeconds },
  })
}

/** `PUT /api/account/presence-timeout` — ADMIN only (D32); bounds enforced server-side. */
export function setPresenceTimeout(presenceTimeoutSeconds: number): Promise<AccountSummary> {
  return request<AccountSummary>('/account/presence-timeout', {
    method: 'PUT',
    body: { presence_timeout_seconds: presenceTimeoutSeconds },
  })
}

/* --- agent file transfers (D31) ------------------------------------------ */

/** `GET /api/zones/{zoneId}/uploads` — the record of files passed; never the bytes. */
export function listUploads(
  zoneId: string,
  params: { page?: number; size?: number } = {},
  signal?: AbortSignal,
): Promise<UploadsPage> {
  const query = new URLSearchParams()
  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.size !== undefined) query.set('size', String(params.size))
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return request<UploadsPage>(`/zones/${seg(zoneId)}/uploads${suffix}`, withSignal(signal))
}

/**
 * `GET /api/zones/{zoneId}/secure-files` — the secure files addressed to ME (SECURE_FILES
 * decision 10). Empty when I hold no connection in the zone, which is the honest answer: a secure
 * file is addressed to an identity, not to an account.
 */
export function listSecureFiles(zoneId: string, signal?: AbortSignal): Promise<SecureFileDto[]> {
  return request<SecureFileDto[]>(`/zones/${seg(zoneId)}/secure-files`, withSignal(signal))
}

/**
 * `GET /api/zones/{zoneId}/secure-files/{handle}/content` — collect one. **This destroys it.**
 *
 * <p>The bytes come back as the response body under my own session; there is deliberately no URL
 * to hold, so no capability token ever reaches page data or React state. The page says the file
 * will be destroyed before the button is pressed.
 */
export function downloadSecureFile(
  zoneId: string,
  handle: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string | undefined }> {
  return requestDownload(
    `/zones/${seg(zoneId)}/secure-files/${seg(handle)}/content`,
    withSignal(signal),
  )
}

/** `GET /api/zones/{zoneId}/uploads/{handle}/downloads` — every attempt, complete or not. */
export function listUploadDownloads(
  zoneId: string,
  handle: string,
  signal?: AbortSignal,
): Promise<DownloadEventDto[]> {
  return request<DownloadEventDto[]>(
    `/zones/${seg(zoneId)}/uploads/${seg(handle)}/downloads`,
    withSignal(signal),
  )
}

/**
 * `DELETE /api/zones/{zoneId}/uploads/{handle}` — ADMIN only (D31 decision 9a).
 * The object goes immediately; the record stays. 404 unknown, 409 already
 * removed, 502 when the object store refused (nothing changed in that case).
 */
export function removeUpload(zoneId: string, handle: string): Promise<void> {
  return request<void>(`/zones/${seg(zoneId)}/uploads/${seg(handle)}`, { method: 'DELETE' })
}

/**
 * `PUT /api/account/upload-limit` — ADMIN only. `null` clears the override and
 * the plan maximum applies; a value above the plan is refused with 400.
 */
export function setUploadLimit(maxUploadBytes: number | null): Promise<AccountSummary> {
  return request<AccountSummary>('/account/upload-limit', {
    method: 'PUT',
    body: { max_upload_bytes: maxUploadBytes },
  })
}

/** `PUT /api/account/blocked-mime-types` — ADMIN only; replaces the whole set. */
export function setBlockedMimeTypes(patterns: string[]): Promise<AccountSummary> {
  return request<AccountSummary>('/account/blocked-mime-types', {
    method: 'PUT',
    body: { patterns },
  })
}

/** `GET .../police/models` — the selectable catalogue, cheapest first. */
export function getPoliceModels(zoneId: string, signal?: AbortSignal): Promise<PoliceModelOption[]> {
  return request<PoliceModelOption[]>(`/zones/${seg(zoneId)}/police/models`, withSignal(signal))
}

export function clearPoliceApiKey(zoneId: string): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police/api-key`, { method: 'DELETE' })
}

/**
 * `POST /api/zones/{zoneId}/police/api-key/verify`
 *
 * Re-checks the stored key. Verification is a snapshot, not a property: a
 * credential that worked in March can be revoked in April, and this is also the
 * only way back from `UNREACHABLE`.
 */
export function verifyPoliceApiKey(zoneId: string): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police/api-key/verify`, { method: 'POST' })
}

/**
 * `POST /api/zones/{zoneId}/police/designate` — promote a connected agent.
 *
 * `acknowledged: true` is not optional and is not decoration: the server refuses
 * without it, so the responsibility transfer is recorded rather than assumed.
 * Only the confirmation modal may call this.
 */
export function designatePolice(zoneId: string, connectionId: string): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police/designate`, {
    method: 'POST',
    body: { connection_id: connectionId, acknowledged: true },
  })
}

/** `POST /api/zones/{zoneId}/police/resign` — empties the seat. Idempotent. */
export function resignPolice(zoneId: string): Promise<PoliceSettings> {
  return request<PoliceSettings>(`/zones/${seg(zoneId)}/police/resign`, { method: 'POST' })
}

/* --- add-ons (§19) -------------------------------------------------------- */

/** `GET /api/account/addons` — the whole catalogue with this account's enrolment. */
export function listAddons(signal?: AbortSignal): Promise<AccountAddon[]> {
  return request<AccountAddon[]>('/account/addons', withSignal(signal))
}

/** `PUT /api/account/addons/{code}` (ADMIN) — 403 for a plain USER, 404 for an unknown code. */
export function setAddon(code: string, enabled: boolean): Promise<AccountAddon> {
  return request<AccountAddon>(`/account/addons/${seg(code)}`, {
    method: 'PUT',
    body: { enabled },
  })
}

/* --- human presence ------------------------------------------------------- */

/** `GET /api/zones/{zoneId}/presence` — my HUMAN connection, or 404. */
export function getPresence(zoneId: string, signal?: AbortSignal): Promise<ConnectionSummary> {
  return request<ConnectionSummary>(`/zones/${seg(zoneId)}/presence`, withSignal(signal))
}

/** `POST /api/zones/{zoneId}/presence` -> 201, joins the zone as a human. */
export function joinZone(zoneId: string, body: JoinZoneRequest = {}): Promise<ConnectionSummary> {
  return request<ConnectionSummary>(`/zones/${seg(zoneId)}/presence`, { method: 'POST', body })
}

/** `DELETE /api/zones/{zoneId}/presence` — leave. */
export async function leaveZone(zoneId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/presence`, { method: 'DELETE' })
}

/* --- messaging ------------------------------------------------------------ */

/**
 * `GET /api/zones/{zoneId}/messages` — ONE PAGE of history, oldest first within the page.
 *
 * Pass `before` to walk back into older history, `after` to walk forwards after the jump rail has
 * re-anchored the window. Naming both is a 400: they page in opposite directions.
 *
 * `channel`: omit for everything, `'none'` for direct messages only, or a channel UUID. This is a
 * SERVER-SIDE filter over which rows exist in the response — unrelated to the client-side sender
 * filter in `pages/agent-visibility.ts`, which only hides rows already fetched.
 */
export function listMessages(
  zoneId: string,
  params: {
    before?: number
    after?: number
    limit?: number
    channel?: string
    since?: string
    until?: string
  } = {},
  signal?: AbortSignal,
): Promise<ZoneHistoryPage> {
  const query = new URLSearchParams()
  if (params.before !== undefined) query.set('before', String(params.before))
  if (params.after !== undefined) query.set('after', String(params.after))
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.channel !== undefined) query.set('channel', params.channel)
  // The half-open window `[since, until)`, both optional and both ISO-8601 instants. The rail and
  // the export take the same pair, so the three never disagree about what "this view" is.
  if (params.since !== undefined) query.set('since', params.since)
  if (params.until !== undefined) query.set('until', params.until)
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return request<ZoneHistoryPage>(`/zones/${seg(zoneId)}/messages${suffix}`, withSignal(signal))
}

/**
 * `GET /api/zones/{zoneId}/messages/index` — the jump rail: one entry per message, no bodies.
 *
 * `channel`: omit for everything, `'none'` for direct messages only, or a channel UUID.
 */
export function listMessageIndex(
  zoneId: string,
  channel?: string,
  window: { since?: string; until?: string } = {},
  signal?: AbortSignal,
): Promise<ZoneMessageIndex> {
  const query = new URLSearchParams()
  if (channel !== undefined) query.set('channel', channel)
  if (window.since !== undefined) query.set('since', window.since)
  if (window.until !== undefined) query.set('until', window.until)
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return request<ZoneMessageIndex>(`/zones/${seg(zoneId)}/messages/index${suffix}`, withSignal(signal))
}

/* -------------------------------------------------------------------------- */
/* Managed-police billing (D27)                                                */
/* -------------------------------------------------------------------------- */

/** `GET /api/account/balance`. */
export function getBalance(signal?: AbortSignal): Promise<AccountBalance> {
  return request<AccountBalance>('/account/balance', withSignal(signal))
}

/** `GET /api/account/balance/transactions` — the ledger, newest first. */
export function listBalanceTransactions(signal?: AbortSignal): Promise<BalanceTransactionRow[]> {
  return request<BalanceTransactionRow[]>('/account/balance/transactions', withSignal(signal))
}

/* -------------------------------------------------------------------------- */
/* Billing: the plan subscription and the police money (stripe.md §6-§9)      */
/* -------------------------------------------------------------------------- */

/** `GET /api/billing` (ADMIN). */
export function getBilling(signal?: AbortSignal): Promise<BillingSummary> {
  return request<BillingSummary>('/billing', withSignal(signal))
}

/**
 * `POST /api/billing/checkout`: an Embedded Checkout session for a plan (BI2). The price is resolved
 * server-side; the console mounts `client_secret` with Stripe.js and never leaves our origin.
 */
export function startCheckout(
  planCode: string,
  interval: 'month' | 'year',
  useCheckout = false,
): Promise<SubscribeResult> {
  return request<SubscribeResult>('/billing/checkout', {
    method: 'POST',
    body: { plan_code: planCode, interval, ...(useCheckout ? { use_checkout: true } : {}) },
  })
}

/** `GET /api/billing/config`: the publishable key (never a secret). */
export function getBillingConfig(signal?: AbortSignal): Promise<BillingConfig> {
  return request<BillingConfig>('/billing/config', withSignal(signal))
}

/** `PUT /api/billing/keep-zones`: choose which zones survive the over-limit deadline. */
export function keepZones(zoneIds: string[]): Promise<OverLimit> {
  return request<OverLimit>('/billing/keep-zones', { method: 'PUT', body: { zone_ids: zoneIds } })
}

/**
 * `POST /api/billing/topup`: an Embedded Checkout session for a one-time police top-up. The credit
 * arrives by webhook, never from this call or the return page.
 */
export function startTopUp(amountUsd: number, saveCard: boolean): Promise<CheckoutSession> {
  return request<CheckoutSession>('/billing/topup', {
    method: 'POST',
    body: { amount_usd: amountUsd, save_card: saveCard },
  })
}

/** `GET /api/billing/refundable` (ADMIN): unconsumed paid top-ups, less what requests hold aside. */
export function getRefundable(signal?: AbortSignal): Promise<Refundable> {
  return request<Refundable>('/billing/refundable', withSignal(signal))
}

/** `POST /api/billing/refund-requests` (ADMIN): reserves the amount until a superoperator decides. */
export function createRefundRequest(amountUsd: number, reason: string): Promise<RefundRequest> {
  return request<RefundRequest>('/billing/refund-requests', {
    method: 'POST',
    body: { amount_usd: amountUsd, reason },
  })
}

export function getPoliceRecharge(signal?: AbortSignal): Promise<PoliceRecharge> {
  return request<PoliceRecharge>('/billing/police-recharge', withSignal(signal))
}

export function setPoliceRecharge(settings: {
  enabled: boolean
  threshold_usd?: number
  amount_usd?: number
}): Promise<PoliceRecharge> {
  return request<PoliceRecharge>('/billing/police-recharge', { method: 'PUT', body: settings })
}

/* -------------------------------------------------------------------------- */
/* Conversations, search, archival, deletion (D23-D26)                         */
/* -------------------------------------------------------------------------- */

/**
 * `GET /api/zones/{zoneId}/conversations/index` — the rail.
 *
 * Replaces {@link listMessageIndex}. Paginated and uncapped: one row per conversation rather than
 * per message, so the cap the old index needed could be removed rather than merely raised.
 *
 * `channel`: omit for everything, `'none'` for direct conversations only, or a channel UUID.
 */
export function listConversations(
  zoneId: string,
  params: {
    archived?: boolean
    page?: number
    size?: number
    channel?: string
    since?: string
    until?: string
  } = {},
  signal?: AbortSignal,
): Promise<ConversationIndex> {
  const query = new URLSearchParams()
  if (params.archived !== undefined) query.set('archived', String(params.archived))
  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.size !== undefined) query.set('size', String(params.size))
  if (params.channel !== undefined) query.set('channel', params.channel)
  // Ignored by the server when `archived=true`: the archive is its own screen, not a filter.
  if (params.since !== undefined) query.set('since', params.since)
  if (params.until !== undefined) query.set('until', params.until)
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return request<ConversationIndex>(
    `/zones/${seg(zoneId)}/conversations/index${suffix}`,
    withSignal(signal),
  )
}

/**
 * `GET /api/zones/{zoneId}/search?q=…` — full-text search, grouped by conversation.
 *
 * `archived` is a HARD SPLIT rather than a filter: false is the live view, true is the archive
 * page, and there is deliberately no value meaning both.
 */
export function searchMessages(
  zoneId: string,
  query: string,
  archived: boolean,
  signal?: AbortSignal,
): Promise<SearchResults> {
  const params = new URLSearchParams({ q: query, archived: String(archived) })
  return request<SearchResults>(`/zones/${seg(zoneId)}/search?${params.toString()}`, withSignal(signal))
}

/** `POST …/conversations/{id}/archive`. Idempotent; writes one row and touches no message. */
/**
 * `POST /api/account/close` — closes the account, irreversibly.
 *
 * `acknowledged` is sent explicitly and checked server-side: the confirmation
 * dialog is an affordance, and an affordance is not a control. Closing retires
 * the account and drops every live connection; it deletes nothing.
 */
export function closeAccount(): Promise<AccountSummary> {
  return request<AccountSummary>('/account/close', {
    method: 'POST',
    body: { acknowledged: true },
  })
}

export function archiveConversation(zoneId: string, conversationId: string): Promise<void> {
  return request<void>(`/zones/${seg(zoneId)}/conversations/${seg(conversationId)}/archive`, {
    method: 'POST',
  })
}

/** `DELETE …/conversations/{id}/archive` — the resource being removed is the archived FLAG. */
export function unarchiveConversation(zoneId: string, conversationId: string): Promise<void> {
  return request<void>(`/zones/${seg(zoneId)}/conversations/${seg(conversationId)}/archive`, {
    method: 'DELETE',
  })
}

/**
 * `POST …/conversations/{id}/delete` — QUEUES a deletion.
 *
 * POST, not DELETE, and the response says when it becomes irreversible. Nothing is gone when this
 * resolves; the request sits for 72 hours and can be cancelled throughout.
 */
export function requestConversationDeletion(
  zoneId: string,
  conversationId: string,
  body: DeleteRequest,
): Promise<DeletionRequested> {
  return request<DeletionRequested>(
    `/zones/${seg(zoneId)}/conversations/${seg(conversationId)}/delete`,
    { method: 'POST', body },
  )
}

/**
 * `GET /api/zones/{zoneId}/messages.csv` — the whole conversation as a file.
 *
 * Fetched rather than linked: the endpoint is authenticated, and a plain `<a href>` cannot carry
 * the bearer token. The SERVER still streams the rows as it reads them, so its memory stays flat
 * however long the conversation is; only the finished file is held browser-side, and CSV text is a
 * fraction of the JSON the old unpaginated history endpoint returned.
 *
 * `channel`: omit for everything, `'none'` for direct messages only, or a channel UUID. The export
 * must narrow identically to the on-screen view — an export containing rows the page does not show
 * is a bug.
 */
export function downloadMessagesCsv(
  zoneId: string,
  channel?: string,
  window: { since?: string; until?: string } = {},
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string | undefined }> {
  const query = new URLSearchParams()
  if (channel !== undefined) query.set('channel', channel)
  // The export must narrow with the view in TIME as well as by channel -- a CSV covering a range
  // the page is not showing is the same bug as one covering a channel it is not showing.
  if (window.since !== undefined) query.set('since', window.since)
  if (window.until !== undefined) query.set('until', window.until)
  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return requestDownload(`/zones/${seg(zoneId)}/messages.csv${suffix}`, withSignal(signal))
}

/**
 * `POST /api/zones/{zoneId}/messages` — 409 when I have no HUMAN presence.
 *
 * Returns per-recipient outcomes: a 200 can still carry failures, because a name
 * that disconnected between the list and the send is reported rather than
 * failing the whole request.
 */
export function sendMessage(
  zoneId: string,
  body: SendMessageRequest,
): Promise<SendMessageResponse> {
  return request<SendMessageResponse>(`/zones/${seg(zoneId)}/messages`, { method: 'POST', body })
}

/** `GET /api/zones/{zoneId}/inbox` — my HUMAN queue, peeked non-destructively. */
export function getInbox(zoneId: string, signal?: AbortSignal): Promise<QueuedMessage[]> {
  return request<QueuedMessage[]>(`/zones/${seg(zoneId)}/inbox`, withSignal(signal))
}

/* --- channels ------------------------------------------------------------- */

/** `GET /api/zones/{zoneId}/channels` */
export function listChannels(zoneId: string, signal?: AbortSignal): Promise<ChannelSummary[]> {
  return request<ChannelSummary[]>(`/zones/${seg(zoneId)}/channels`, withSignal(signal))
}

/** `POST /api/zones/{zoneId}/channels` — created by my HUMAN connection (409 without one). */
export function createChannel(zoneId: string, body: CreateChannelRequest): Promise<ChannelSummary> {
  return request<ChannelSummary>(`/zones/${seg(zoneId)}/channels`, { method: 'POST', body })
}

/** `POST /api/zones/{zoneId}/channels/{id}/join` — my HUMAN connection joins. */
export async function joinChannel(zoneId: string, id: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/channels/${seg(id)}/join`, { method: 'POST' })
}

/** `DELETE /api/zones/{zoneId}/channels/{id}` (ADMIN) */
export async function deleteChannel(zoneId: string, id: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/channels/${seg(id)}`, { method: 'DELETE' })
}

/** `GET /api/zones/{zoneId}/channels/{channelId}/members` — who is joined right now. */
export function listChannelMembers(
  zoneId: string,
  channelId: string,
  signal?: AbortSignal,
): Promise<ChannelMember[]> {
  return request<ChannelMember[]>(
    `/zones/${seg(zoneId)}/channels/${seg(channelId)}/members`,
    withSignal(signal),
  )
}

/** `POST /api/zones/{zoneId}/channels/{channelId}/leave` -> 204, idempotent. */
export async function leaveChannel(zoneId: string, channelId: string): Promise<void> {
  await request<void>(`/zones/${seg(zoneId)}/channels/${seg(channelId)}/leave`, { method: 'POST' })
}

/* --- tooling (per zone — §16) --------------------------------------------- */

/** `GET /api/zones/{zoneId}/tooling/mcp.json` — already carries this zone's URL. */
export function getZoneMcpJson(zoneId: string, signal?: AbortSignal): Promise<string> {
  return requestText(`/zones/${seg(zoneId)}/tooling/mcp.json`, {
    accept: 'application/json, text/plain',
    ...withSignal(signal),
  })
}

export type InstallerKind = 'sh' | 'ps1'

const INSTALLER_FILENAMES: Record<InstallerKind, string> = {
  sh: 'install.sh',
  ps1: 'install.ps1',
}

/**
 * `GET /api/zones/{zoneId}/tooling/skills/{vendor}.zip`
 *
 * The standing instructions that teach an LLM agent to coordinate through this
 * zone, wrapped in whatever file the chosen runtime actually loads. Contains no
 * secret -- the MCP config inside references `${SALTSEED_TOKEN}`.
 */
export async function downloadZoneSkillBundle(
  zoneId: string,
  vendor: SkillVendor,
): Promise<{ blob: Blob; filename: string }> {
  const { blob, filename } = await requestDownload(
    `/zones/${seg(zoneId)}/tooling/skills/${vendor}.zip`,
  )
  return { blob, filename: filename ?? `saltseed-${vendor}-skill.zip` }
}

/** `GET /api/zones/{zoneId}/tooling/install.sh` | `install.ps1` */
export async function downloadZoneInstaller(
  zoneId: string,
  kind: InstallerKind,
): Promise<{ blob: Blob; filename: string }> {
  const { blob, filename } = await requestDownload(
    `/zones/${seg(zoneId)}/tooling/${INSTALLER_FILENAMES[kind]}`,
  )
  return { blob, filename: filename ?? INSTALLER_FILENAMES[kind] }
}

/* --- tooling (per server, CB-WI9) ------------------------------------------ */

/**
 * `GET /tooling/info` — `BridgeController`, public and deliberately outside `/api` (see
 * `env.toolingRootUrl`). Unauthenticated on purpose: an installer running on a machine with a
 * zone token and no console session must be able to read this too.
 */
export function getToolingInfo(signal?: AbortSignal): Promise<ToolingInfo> {
  return request<ToolingInfo>(`${env.toolingRootUrl}/tooling/info`, {
    authenticated: false,
    ...withSignal(signal),
  })
}

/**
 * The one bridge tarball this build ships, by exact version — `GET
 * /tooling/bridge-<version>.tgz`. A plain URL builder, not a fetch: the Skills page links to it
 * directly so the browser drives the download (and the server's `Content-Disposition`, not this
 * function, names the saved file).
 */
export function bridgeTarballUrl(version: string): string {
  return `${env.toolingRootUrl}/tooling/bridge-${encodeURIComponent(version)}.tgz`
}

/* --- feedback ------------------------------------------------------------- */

/** `POST /api/feedback` — any signed-in user. 400 invalid, 404 unknown zone, 429 daily limit. */
export function submitFeedback(body: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> {
  return request<SubmitFeedbackResponse>('/feedback', { method: 'POST', body })
}

/** The staff inbox's filters, as the query string takes them. Empty values are omitted. */
export type StaffFeedbackFilters = Partial<
  Record<'category' | 'state' | 'zone' | 'client' | 'since' | 'until' | 'q', string>
>

function feedbackQuery(filters: StaffFeedbackFilters, extra: Record<string, string> = {}): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries({ ...filters, ...extra })) {
    if (value !== undefined && value !== '') query.set(key, value)
  }
  return query.size > 0 ? `?${query.toString()}` : ''
}

/** `GET /api/staff/feedback` — 403 unless the caller is staff. List items carry no body. */
export function listStaffFeedback(
  filters: StaffFeedbackFilters,
  cursor?: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<StaffFeedbackPage> {
  const extra: Record<string, string> = { limit: String(limit) }
  if (cursor !== undefined) extra['cursor'] = cursor
  return request<StaffFeedbackPage>(`/staff/feedback${feedbackQuery(filters, extra)}`, withSignal(signal))
}

/** `GET /api/staff/feedback/{id}` — the full item, body included. */
export function getStaffFeedback(id: string, signal?: AbortSignal): Promise<StaffFeedbackItem> {
  return request<StaffFeedbackItem>(`/staff/feedback/${seg(id)}`, withSignal(signal))
}

/** `PATCH /api/staff/feedback/{id}` */
export function triageFeedback(
  id: string,
  body: TriageFeedbackRequest,
): Promise<StaffFeedbackItem> {
  return request<StaffFeedbackItem>(`/staff/feedback/${seg(id)}`, { method: 'PATCH', body })
}

/** `GET /api/staff/feedback.csv` — the same filters as the list, so the export matches the view. */
export function downloadFeedbackCsv(
  filters: StaffFeedbackFilters,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string | undefined }> {
  return requestDownload(`/staff/feedback.csv${feedbackQuery(filters)}`, withSignal(signal))
}

/* -------------------------------------------------------------------------- */
/* Billing in the app (2026-10-BILLING-IN-APP): invoices, cards, plan changes  */
/* -------------------------------------------------------------------------- */

/** `GET /api/billing/invoices`: newest first, Stripe cursor paging. */
export function listInvoices(
  params: { cursor?: string | null; limit?: number; kind?: InvoiceKind | null },
  signal?: AbortSignal,
): Promise<InvoicePage> {
  const query = new URLSearchParams()
  if (params.cursor) query.set('cursor', params.cursor)
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.kind) query.set('kind', params.kind)
  const suffix = query.size === 0 ? '' : `?${query.toString()}`
  return request<InvoicePage>(`/billing/invoices${suffix}`, withSignal(signal))
}

/** An authenticated PDF fetch for our own `document_path` / invoice / credit-note route. */
export function downloadBillingPdf(path: string): Promise<{ blob: Blob; filename: string | undefined }> {
  return requestDownload(path.replace(/^\/api(?=\/)/, ''), { accept: 'application/pdf' })
}

export const invoicePdfPath = (invoiceId: string): string => `/billing/invoices/${seg(invoiceId)}/pdf`
export const creditNotePdfPath = (creditNoteId: string): string => `/billing/credit-notes/${seg(creditNoteId)}/pdf`

/** `POST /api/billing/invoices/{id}/pay`: 402 `payment_declined` carries Stripe's sentence. */
export function payInvoice(invoiceId: string, paymentMethod?: string): Promise<Invoice> {
  return request<Invoice>(`/billing/invoices/${seg(invoiceId)}/pay`, {
    method: 'POST',
    body: paymentMethod === undefined ? {} : { payment_method: paymentMethod },
  })
}

/** `GET /api/billing/upcoming`: the next invoice preview and any scheduled change. */
export function getUpcoming(signal?: AbortSignal): Promise<UpcomingInvoice> {
  return request<UpcomingInvoice>('/billing/upcoming', withSignal(signal))
}

export function listPaymentMethods(signal?: AbortSignal): Promise<PaymentMethodCard[]> {
  return request<PaymentMethodCard[]>('/billing/payment-methods', withSignal(signal))
}

/** `POST /api/billing/setup-intent`: the client secret the Payment Element confirms. */
export function createSetupIntent(): Promise<{ client_secret: string }> {
  return request<{ client_secret: string }>('/billing/setup-intent', { method: 'POST' })
}

export function setDefaultPaymentMethod(id: string): Promise<PaymentMethodCard[]> {
  return request<PaymentMethodCard[]>(`/billing/payment-methods/${seg(id)}/default`, { method: 'POST' })
}

/** 409 `last_payment_method` while a live subscription or auto-recharge depends on the card. */
export function removePaymentMethod(id: string): Promise<PaymentMethodCard[]> {
  return request<PaymentMethodCard[]>(`/billing/payment-methods/${seg(id)}`, { method: 'DELETE' })
}

export function getBillingDetails(signal?: AbortSignal): Promise<BillingDetails> {
  return request<BillingDetails>('/billing/details', withSignal(signal))
}

export function updateBillingDetails(body: { name: string; address: BillingAddress }): Promise<BillingDetails> {
  return request<BillingDetails>('/billing/details', { method: 'PUT', body })
}

export function listTaxIds(signal?: AbortSignal): Promise<TaxId[]> {
  return request<TaxId[]>('/billing/tax-ids', withSignal(signal))
}

export function addTaxId(body: { type: string; value: string }): Promise<TaxId> {
  return request<TaxId>('/billing/tax-ids', { method: 'POST', body })
}

export function deleteTaxId(id: string): Promise<void> {
  return request<void>(`/billing/tax-ids/${seg(id)}`, { method: 'DELETE' })
}

export function previewPlanChange(plan: string, interval: 'month' | 'year'): Promise<PlanChangePreview> {
  return request<PlanChangePreview>('/billing/subscription/preview', { method: 'POST', body: { plan, interval } })
}

export function changePlanNow(plan: string, interval: 'month' | 'year'): Promise<PlanChangeResult> {
  return request<PlanChangeResult>('/billing/subscription/change', { method: 'POST', body: { plan, interval } })
}

export function cancelSubscription(): Promise<SubscriptionState> {
  return request<SubscriptionState>('/billing/subscription/cancel', { method: 'POST' })
}

export function resumeSubscription(): Promise<SubscriptionState> {
  return request<SubscriptionState>('/billing/subscription/resume', { method: 'POST' })
}

export function undoScheduledChange(): Promise<void> {
  return request<void>('/billing/subscription/scheduled-change', { method: 'DELETE' })
}
